import { CustomerInfo } from 'app/core/store/customer-info/customer-info.interface';
import { INITIAL_STATE_CUSTOMER_INFO } from 'app/core/store/customer-info/customer-info.model';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { util } from 'app/core/util/util';
import moment from 'moment';
import * as _ from 'typedash';
import * as customerPermissionsActions from '../session/customer-permissions/customer-permissions.actions';
import * as actions from './customer-info.actions';

const fixArrayOrObjectIssue = arrayOrObject => {
  if (!arrayOrObject) return [];
  if (Array.isArray(arrayOrObject)) return arrayOrObject;
  else return [arrayOrObject];
};

export function reducer(state = INITIAL_STATE_CUSTOMER_INFO, action: actions.Actions | customerPermissionsActions.Actions): CustomerInfo {
  switch (action.type) {
    case actions.CUSTOMER_INFO_LOAD:
      return {
        ...state,
        loading: true,
        loaded: false,
        customer: action.payload.customer,
      };

    case actions.CUSTOMER_INFO_RESET:
      return {
        ...state,
        loading: false,
        loaded: false,
        customer: null,
      };

    case actions.CUSTOMER_INFO_LOAD_FAIL:
      return {
        ...state,
        loaded: false,
        loading: false,
      };

    case actions.CUSTOMER_INFO_LOAD_SUCCESS:
      // If superuser, don't overwrite restrictions when loading customerInfo for create order/flow
      const restrictions = action.payload.isSuperUser
        ? state.restrictions
        : {
          ...state.restrictions,
          blankReceiptPossible: !!action.payload.customerInfo.blankReceiptPossible,
          deviationManagement: !!action.payload.customerInfo.deviationManagement,
          GS1LineRef: !!action.payload.customerInfo.GS1LineRef,
          useCcrValidation: !!action.payload.customerInfo.useCcrValidation,
          overruleMaxQty: !!action.payload.customerInfo.overruleMaxQty,
          palletExchange: !!action.payload.customerInfo.palletExchange,
          sealNumberRequired: !!action.payload.customerInfo.sealNumberRequired,
        };
      const shipTos = Object.keys(action.payload.customerInfo.shipTos).map(id => {
        return {
          id,
          name: action.payload.customerInfo.shipTos[id],
        };
      });
      const customerInfo = {
        ...state,
        places: {},
        defaults: {
          localCurrency: action.payload.customerInfo.defaults.localCurrency,
          // transport: '',
          depot: null,
        },
        restrictions,
        orderTypes: action.payload.customerInfo.orderTypes.map(
          id =>
            action.payload.definitions.order.type.find(t => t.id === id) || {
              id,
              name: id,
            }
        ),
        flowRegistrationTypes: action.payload.customerInfo.flowRegistrationTypes.map(
          id =>
            action.payload.definitions.flow.type.find(t => t.id === id) || {
              id,
              name: id,
            }
        ),
        flowHandshakeTypes: action.payload.customerInfo.flowHandshakeTypes.map(
          id =>
            action.payload.definitions.flow.type.find(t => t.id === id) || {
              id,
              name: id,
            }
        ),
        allFlowTypes: [],
        loaded: true,
        loading: false,
        customer: {
          ...state.customer,
          shipTos,
        },
      };

      customerInfo.orderTypes.sort(util.idSorter());
      customerInfo.flowRegistrationTypes.sort(util.idSorter());
      customerInfo.flowHandshakeTypes.sort(util.idSorter());

      // Remove duplicates
      const allFlowTypes = [...customerInfo.flowRegistrationTypes, ...customerInfo.flowHandshakeTypes].reduce((res, type) => {
        return {
          ...res,
          [type.id]: type as Definition,
        };
      }, {});
      customerInfo.allFlowTypes = Object.keys(allFlowTypes).map(key => allFlowTypes[key]); // Typescript doesn't have Object.values(...)

      customerInfo.salesOrganisation = {
        id: action.payload.customerInfo.salesOrganisation.id,
        contacts: action.payload.customerInfo.salesOrganisation.contacts?.map(c => ({
          key: 'DASHBOARD.LOCAL_OFFICE.CONTACT.' + c.Label,
          emailAddress: c.Email,
          phoneNumber: c.PhoneNumber,
        })) || [],
      };

      if (action.payload.customerInfo.defaults.depot) {
        const { depot } = action.payload.customerInfo.defaults;
        const openingHours = depot.openingHours && JSON.parse(depot.openingHours);
        const address = depot.address && JSON.parse(depot.address);

        const defaultDepot = {
          ...depot,
          ...address,
          default: true,
          isDepot: depot.type.toLocaleLowerCase() === 'depot',
          isLocation: false,
          isCustomer: false,
          openingHours: _.compact(
            _.flatten(openingHours && openingHours.length && openingHours[0]['Day']).map(day => {
              return (
                day && {
                  weekday: day['weekday'],
                  weekdayNr: _.indexOf(['SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA'], day['weekday']), // ROALD guarantees that this will be stable, it won't change to another language all of the sudden
                  slots: _.compact([day['Morning'], day['Afternoon']]).map(slot => {
                    return {
                      from: moment(slot.from, 'H:mm:ss').format('H[h]mm'),
                      to: moment(slot.to, 'H:mm:ss').format('H[h]mm'),
                    };
                  }),
                }
              );
            })
          ),
        };

        customerInfo.places = {
          [defaultDepot.id]: defaultDepot,
        };
      }

      return customerInfo;

    case customerPermissionsActions.CUSTOMER_PERMISSIONS_LOAD_SUCCESS:
      return {
        ...state,
        restrictions: {
          ...state.restrictions,
          blankReceiptPossible: action.payload.customerPermissions.blankReceipts.length > 0,
        },
      } as CustomerInfo;

    default:
      return state;
  }
}
